import React, {useState, useEffect, useRef} from "react";

import HeroSection from "../../components/molecules/Hero/HeroSection";
import TestimonialGrid from "../../components/molecules/Testimonials/TestimonialGrid";
import MagneticButton from "../../components/atoms/buttons/MagneticButton";
import useScrollAnimation from "../../hooks/useScrollAnimation";
import {FriendsList} from "./FriendsList";
import titov from "../../assets/titov.jpg";

// Animated counter component for stats
const CountUp = ({end, duration = 2000, suffix = ""}) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation({threshold: 0.5});
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

// Combined About + SkinSort section - more personal, less corporate
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation({threshold: 0.15});

  return (
    <section className="pt-20 pb-10 px-4 bg-cloud-100">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* The story */}
        <div className="space-y-8 text-lg text-earth-700 leading-relaxed">
          <p>
            <a
              href="https://www.skinsort.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pine-700 hover:text-pine-800 underline underline-offset-2"
            >
              SkinSort
            </a>{" "}
            has been the biggest challenge, yet the most fulfilling endeavor
            I've ever taken on. It has required thousands of hours, boundless
            passion, some tears, and a lot of love. Watching it grow has made
            every moment worth it.
          </p>
        </div>

        {/* Simple CTA */}
        <div className="mt-12 flex justify-center">
          <MagneticButton href="https://www.skinsort.com/">
            Check out SkinSort
          </MagneticButton>
        </div>
      </div>
    </section>
  );
};

// Dark themed footer with Mewtwo quote
const FooterSection = () => {
  const [ref, isVisible] = useScrollAnimation({threshold: 0.3});

  return (
    <footer className="bg-pine-900 py-20 px-4">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Quote */}
        <blockquote className="text-xl sm:text-2xl text-white/80 font-lato italic leading-relaxed">
          "I see now that the circumstances of one's birth are irrelevant… It is
          what you do with the gift of life that determines who you are."
        </blockquote>
        <cite className="block mt-6 text-moss-400 font-medium not-italic">
          — Mewtwo
        </cite>

        {/* Copyright */}
        <p className="mt-8 text-white/30 text-sm">
          © {new Date().getFullYear()} Genne Liu
        </p>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  return (
    <div className="w-full bg-cloud-50">
      {/* Hero - Full screen with mountain background */}
      <HeroSection
        backgroundImage={titov}
        title="Hi, I'm Genne"
        subtitle="building, exploring, learning always"
      />
      {/* About - Combined personal story */}
      <AboutSection />
      {/*  Testimonials     /* */}
      <TestimonialGrid testimonials={FriendsList} />
      {/* Footer - Dark theme with quote */}
      <FooterSection />
    </div>
  );
};

export default LandingPage;
